// Power Play report: a point-by-point table per set (score progression,
// who served each point, running differential, and the Power Play windows
// highlighted). Only meaningful for matches that actually used a Power Play —
// Session.jsx only shows this report tab when `match.hasPowerPlays` is true
// (see DVWFile.js calculateDVWStats).
import { useState } from "react";
import { useLanguage } from "../../context/Language/LanguageContext";
import { buildPointByPoint } from "../utils/PointByPoint";

const kHomeColour = "#2563eb";
const kAwayColour = "#f97316";

function SetPointByPointCard({ set, setNumber, teamAName, teamBName, t }) {
  const points = buildPointByPoint(set);
  const hasPowerPlay = points.some((p) => (p.powerPlayTeams ?? []).length > 0);
  if (!hasPowerPlay) return null;

  const finalPoint = points[points.length - 1];
  const bonusCount = points.filter((p) => p.isBonus).length;

  return (
    <div className="card bg-base-200 shadow-sm">
      <div className="card-body p-4">
        <h3 className="font-bold text-sm uppercase">
          {t("Point by Point")} — {t("Set")} {setNumber}{" "}
          <span className="opacity-60 font-normal normal-case">
            ({finalPoint.pointsA}-{finalPoint.pointsB}
            {bonusCount > 0
              ? ` — ${bonusCount} ${bonusCount === 1 ? t("bonus point") : t("bonus points")}`
              : ""}
            )
          </span>
        </h3>
        <div className="overflow-x-auto mt-2">
          {/* No table-zebra: its even-row background has higher CSS specificity
              than our per-row bg-warning/20 and would silently hide the
              Power Play highlight on alternating rows. */}
          <table className="table table-xs w-auto">
            <thead>
              <tr className="text-[10px]">
                <th className="text-center px-1">HP</th>
                <th colSpan={3} className="text-center px-1" style={{ color: kHomeColour }}>
                  {teamAName}
                </th>
                <th className="px-1" />
                <th colSpan={3} className="text-center px-1" style={{ color: kAwayColour }}>
                  {teamBName}
                </th>
                <th className="text-center px-1">AP</th>
              </tr>
            </thead>
            <tbody>
              {points.map((p) => {
                const diff = p.pointsA - p.pointsB;
                const inPowerPlay = p.powerPlayTeams.length > 0;
                const homeBonus = p.isBonus && p.scoringTeam === "A";
                const awayBonus = p.isBonus && p.scoringTeam === "B";
                return (
                  <tr key={p.index} className={inPowerPlay ? "bg-warning/20" : ""}>
                    <td className="text-center px-1">
                      {p.powerPlayTeams.includes("A") && (
                        <span className={`badge badge-xs ${homeBonus ? "badge-warning font-bold" : "badge-ghost"}`}>
                          HP
                        </span>
                      )}
                    </td>
                    <td className="text-right font-mono px-1">{diff > 0 ? `+${diff}` : ""}</td>
                    <td className="text-center px-1" style={{ color: kHomeColour }}>
                      {p.server === "A" ? "●" : ""}
                    </td>
                    <td className={`text-right font-mono px-1 ${homeBonus ? "font-bold text-warning" : ""}`}>
                      {p.pointsA}
                    </td>
                    <td className="text-center opacity-50 px-1">-</td>
                    <td className={`text-left font-mono px-1 ${awayBonus ? "font-bold text-warning" : ""}`}>
                      {p.pointsB}
                    </td>
                    <td className="text-center px-1" style={{ color: kAwayColour }}>
                      {p.server === "B" ? "●" : ""}
                    </td>
                    <td className="text-left font-mono px-1">{diff < 0 ? `+${-diff}` : ""}</td>
                    <td className="text-center px-1">
                      {p.powerPlayTeams.includes("B") && (
                        <span className={`badge badge-xs ${awayBonus ? "badge-warning font-bold" : "badge-ghost"}`}>
                          AP
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PowerPlayReport({ matches }) {
  const { t } = useLanguage();
  const [activeIdx, setActiveIdx] = useState(0);
  const match = matches?.[0];
  if (!match) return null;
  const sets = match.sets ?? match.drills ?? [];

  if (!match.hasPowerPlays) {
    return (
      <div className="alert alert-info m-4">
        <span>{t("This match has no Power Play calls.")}</span>
      </div>
    );
  }

  // Only sets that actually used a Power Play get a card/tab.
  const powerPlaySets = sets
    .map((set, idx) => ({ set, setNumber: idx + 1 }))
    .filter(({ set }) => buildPointByPoint(set).some((p) => (p.powerPlayTeams ?? []).length > 0));

  if (powerPlaySets.length === 0) {
    return (
      <div className="alert alert-info m-4">
        <span>{t("This match has no Power Play calls.")}</span>
      </div>
    );
  }

  const active = powerPlaySets[Math.min(activeIdx, powerPlaySets.length - 1)];

  return (
    <div className="flex flex-col gap-4 p-4">
      <p className="text-xs opacity-70">
        {t("● marks which team served the point.")}{" "}
        {t("Shaded rows are played under a Power Play — HP/AP marks the calling team, bold gold scores are bonus (double) points.")}
      </p>

      {/* Small screens: one set at a time via tabs. */}
      <div className="lg:hidden">
        <div role="tablist" className="tabs tabs-boxed flex-wrap mb-2">
          {powerPlaySets.map(({ setNumber }, i) => (
            <button
              key={setNumber}
              type="button"
              role="tab"
              className={"tab tab-sm" + (i === activeIdx ? " tab-active" : "")}
              onClick={() => setActiveIdx(i)}
            >
              {t("Set")} {setNumber}
            </button>
          ))}
        </div>
        <SetPointByPointCard
          set={active.set}
          setNumber={active.setNumber}
          teamAName={match.teamA.Name}
          teamBName={match.teamB.Name}
          t={t}
        />
      </div>

      {/* Larger screens: fit as many sets side-by-side as the window allows. */}
      <div className="hidden lg:grid lg:grid-cols-2 2xl:grid-cols-3 gap-4 items-start">
        {powerPlaySets.map(({ set, setNumber }) => (
          <SetPointByPointCard
            key={setNumber}
            set={set}
            setNumber={setNumber}
            teamAName={match.teamA.Name}
            teamBName={match.teamB.Name}
            t={t}
          />
        ))}
      </div>
    </div>
  );
}

export default PowerPlayReport;
